import React, { useState, useCallback } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import CheckoutForm from "./CheckoutForm"; // Import UI component
import API_BASE_URL from "./config"; // Use API_BASE_URL from config

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

const Checkout = () => {
  const [email, setEmail] = useState("");
  const [isVerified, setIsVerified] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [showVerificationInput, setShowVerificationInput] = useState(false);
  const [verificationMessage, setVerificationMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [emailMessage, setEmailMessage] = useState("");  // ✅ For email-related errors
  const [codeMessage, setCodeMessage] = useState("");   // ✅ For verification code errors
  const [paymentSuccess, setPaymentSuccess] = useState(false); // ✅ Track if payment was successful
  const [companyLogo, setCompanyLogo] = useState("");
  //const [isTermsAccepted, setIsTermsAccepted] = useState(false);

  // ✅ Email Verification Logic (Auto-Sanitized Input)
  const verifyEmail = useCallback(async () => {
    const sanitizedEmail = email.trim().toLowerCase();

    if (!sanitizedEmail.includes("@") || !sanitizedEmail.includes(".")) {
      setEmailMessage("⚠️ Invalid email format");
      setIsVerified(false);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/verify-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: sanitizedEmail }),
      });

      if (response.status === 403) {
        setEmailMessage("⚠️ Email already exists. Contact support.");
        setIsVerified(false);
      } else {
        setEmailMessage("✅ Verification code sent!");
        setShowVerificationInput(true);
      }
    } catch (error) {
      console.error("❌ Email Verification Error:", error);
      setEmailMessage("❌ Error verifying email. Try again.");
    } finally {
      setLoading(false);
    }
  }, [email]);

  async function fetchCompanyLogo() {
    try {
        const response = await fetch(`${API_BASE_URL}/get-company-logo`);
        const data = await response.json();
        if (data.logo) {
            setCompanyLogo(data.logo);
        }
    } catch (error) {
        console.error("❌ Error fetching company logo:", error);
    }
}
fetchCompanyLogo();


  // ✅ OTP Verification Logic
  const handleVerifyCode = async () => {
    if (!email || !verificationCode) {
      setCodeMessage("❌ Email and verification code are required.");
      return;
    }

    const payload = { email, code: verificationCode };
    console.log("Sending request to /verify-code with payload:", payload);

    try {
      const response = await fetch(`${API_BASE_URL}/verify-code`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      console.log("Response from server:", data);

      if (response.ok) {
        setIsVerified(true);
        setShowVerificationInput(false);
        setCodeMessage("✅ Email verified successfully!");
        setVerificationCode(""); // ✅ Clear OTP field on success
        setEmailMessage(""); // ✅ Remove "Verification code sent!" message
      } else {
        setCodeMessage("❌ Invalid verification code.");
      }
    } catch (error) {
      console.error("❌ Verification request failed:", error);
      setCodeMessage("❌ Server error. Please try again.");
    }
  };

  return (
    <Elements stripe={stripePromise}>
      <CheckoutForm
        email={email}
        setEmail={setEmail}
        verifyEmail={verifyEmail}
        isVerified={isVerified}
        verificationCode={verificationCode}
        setVerificationCode={setVerificationCode}
        handleVerifyCode={handleVerifyCode}
        showVerificationInput={showVerificationInput}
        setShowVerificationInput={setShowVerificationInput}
        emailMessage={emailMessage}         // ✅ Pass email message state
        setEmailMessage={setEmailMessage}   // ✅ Pass email message setter
        codeMessage={codeMessage}           // ✅ Pass verification code message state
        setCodeMessage={setCodeMessage}     // ✅ Pass verification code message setter
        paymentSuccess={paymentSuccess}
        setPaymentSuccess={setPaymentSuccess}
        loading={loading}
        companyLogo={companyLogo}
      />
    </Elements>
  );
};

export default Checkout;